import { Map } from "immutable";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import { getCurrentBillingPeriod, getCurrentPlanName } from "~/store/selectors/selector.accountSettings";
import {
  getPaymentMethodCreditCardType,
  getIsCreditCardExpired,
  getSubmittingStripe,
  getSubmittingPaymentMethod,
} from "./selector.billingPaymentMethod.jsx";
import { getUserDefaultHomepage } from "./selector.configOptions.jsx";
import { getAllSubscriptionPlans } from "./selector.subscriptionPlans.jsx";

export const getBillingOrder = (state) => state.billingOrder.billingOrder;
export const getBillingOrderPeriod = (state) => getBillingOrder(state).get(`billing_period`) || `annually`;
export const getBillingOrderProperty = (state, property, defaultValue) => {
  return getBillingOrder(state).get(`${property}`, defaultValue);
};

export const getBillingOrderType = (state) => getBillingOrder(state).get(`type`);

export const getIsChangingPaymentGateway = (state) => getBillingOrder(state).get(`is_changing_payment_gateway`);

export const canSwitchBillingPeriod = (state) => getBillingOrder(state).get(`can_switch_billing_period`);

export const getBillingOrderPlan = (state) => getBillingOrder(state).get(`plan`);

export const getCompletingBillingOrder = (state) => state.billingOrder.completingBillingOrder;

export const getLoadingBillingOrder = (state) => state.billingOrder.loadingBillingOrder;

export const getIsPaymentError = (state) => state.billingOrder.paymentError;

export const getThankYou = (state) => state.billingOrder.thankYou;

// Billing Address Header text selector

export const makeBillingOrderCompleteLoaderSelector = () => {
  return createSelector(
    getSubmittingStripe,
    getSubmittingPaymentMethod,
    getCompletingBillingOrder,
    (submittingStripe, submittingPaymentMethod, completingOrder) => {
      return submittingStripe || submittingPaymentMethod || completingOrder;
    }
  );
};

export const makeBillingOrderPropertySelector = () => {
  return createSelector(getBillingOrderProperty, (billingPeriodPropertyValue) => billingPeriodPropertyValue);
};

export const makeBillingOrderFullPlanNameSelector = () => {
  return createSelector(getBillingOrderPlan, (plan) => {
    switch (plan) {
      case `S`:
        return `Small`;
      case `M`:
        return `Medium`;
      case `L`:
        return `Large`;
      case `XL`:
        return `Extra Large`;
      case `per_seat`:
        return `Per Seat`;
      default:
        return `Mega`;
    }
  });
};

export const makeBillingAddressHeaderTextSelector = () => {
  return createSelector(
    getBillingOrderPeriod,
    getBillingOrderPlan,
    getAllSubscriptionPlans,
    (billingPeriod, plan, planByName) => {
      let saved_amount = null;
      switch (plan) {
        case `S`:
          saved_amount = planByName.get(`S`).get(`annual_savings`);
          break;
        case `M`:
          saved_amount = planByName.get(`M`).get(`annual_savings`);
          break;
        case `L`:
          saved_amount = planByName.get(`L`).get(`annual_savings`);
          break;
        case `XL`:
          saved_amount = planByName.get(`XL`).get(`annual_savings`);
          break;
        case `per_seat`:
          saved_amount = planByName.get(`per_seat`).get(`annual_savings`);
          break;
        default:
          saved_amount = planByName.get(`MEGA`).get(`annual_savings`);
      }

      if (billingPeriod === `annually`) {
        return i18n.t("You have just saved {{saved_amount}} by choosing annual billing.", {
          saved_amount: saved_amount,
        });
      }

      return i18n.t("Choose annual payment and save {{saved_amount}}.", {
        saved_amount: saved_amount,
      });
    }
  );
};

export const makeBillingOrderForOrderSummarySelector = () => {
  const getBillingOrderFullPlanName = makeBillingOrderFullPlanNameSelector();

  return createSelector(
    getBillingOrder,
    (state) => getBillingOrderFullPlanName(state),
    (billingOrder, plan) => {
      if (Map.isMap(billingOrder)) {
        return {
          id: billingOrder.get(`id`),
          type: billingOrder.get(`type`),
          status: billingOrder.get(`status`),
          plan,
          numberOfMembers: billingOrder.get(`number_of_members`) || `∞`,
          billingPeriod: billingOrder.get(`billing_period`),
          initialBillingPeriod: billingOrder.get(`initial_billing_period`),
          total: billingOrder.get(`total`),
          subtotal: billingOrder.get(`subtotal`),
          discount: billingOrder.get(`discount`, 0),
          orderSummary: billingOrder.get(`order_summary`),
          paymentSuccessMessage: billingOrder.get(`payment_success_message`),
        };
      }

      return {};
    }
  );
};

export const makeBillingOrderTaxForOrderSummarySelector = () => {
  return createSelector(getBillingOrder, (billingOrder) => {
    if (Map.isMap(billingOrder)) {
      return {
        taxName: billingOrder.get(`tax_name`),
        taxRate: billingOrder.get(`tax_rate`),
        tax: billingOrder.get(`tax`),
      };
    }

    return {};
  });
};

export const getIsAccountBalanceBillingOrder = createSelector(
  getBillingOrderType,
  (orderType) => orderType === `ActiveCollab\\Module\\OnDemand\\Model\\BillingOrder\\AccountBalanceBillingOrder`
);

export const getIsCreateSubscriptionBillingOrder = createSelector(
  getBillingOrderType,
  (orderType) => orderType === `ActiveCollab\\Module\\OnDemand\\Model\\BillingOrder\\CreateSubscriptionBillingOrder`
);

export const getIsAddOnBillingOrder = createSelector(getBillingOrderType, (orderType) => {
  return (
    orderType === `ActiveCollab\\Module\\OnDemand\\Model\\BillingOrder\\AddOnBillingOrder` ||
    orderType === `ActiveCollab\\Module\\OnDemand\\Model\\BillingOrder\\FullPriceAddOnAndLifetimeActivationBillingOrder`
  );
});

export const shouldShowStripeForm = createSelector(
  getPaymentMethodCreditCardType,
  getIsPaymentError,
  getIsCreditCardExpired,
  (creditCardType, isPaymentError, isCreditCardExpired) => {
    return creditCardType !== `StripePaymentMethod` || isPaymentError || isCreditCardExpired;
  }
);

export const getBillingOrderStep = createSelector(getBillingOrder, (billingOrder) => billingOrder.get(`step`));

export const getBillingOrderTotal = createSelector(getBillingOrder, (billingOrder) => billingOrder.get(`total`, 0.0));

export const getBillingOrderBalance = createSelector(getBillingOrder, (billingOrder) =>
  billingOrder.get(`balance`, 0.0)
);

export const getTotalMinusBalance = createSelector(
  getBillingOrderTotal,
  getBillingOrderBalance,
  (total, balance) => total - balance
);

export const getThankYouHeaderText = createSelector(getThankYou, (thankYou) => thankYou.get(`header`));

export const getThankYouBodyText = createSelector(getThankYou, (thankYou) => thankYou.get(`body`));

export const getThankYouCTAText = createSelector(getThankYou, (thankYou) => thankYou.get(`cta`));

export const getThankYouUrlRedirect = createSelector(getThankYou, getUserDefaultHomepage, (thankYou, defaultPage) =>
  thankYou.get(`url`, defaultPage)
);

export const getBillingOrderStatus = createSelector(getBillingOrder, (billingOrder) => billingOrder.get(`status`));

export const getIsCreateSubscriptionBillingOrderPending = createSelector(
  getIsCreateSubscriptionBillingOrder,
  getBillingOrderStatus,
  (isCreateSubscriptionBillingOrder, orderStatus) => {
    return isCreateSubscriptionBillingOrder && orderStatus === `pending`;
  }
);

export const getIsLegacyFastSpringBillingOrder = createSelector(
  getIsChangingPaymentGateway,
  (orderIsChangingPaymentGateway) => orderIsChangingPaymentGateway === true
);

export const getIsLegacyFastSpringBillingOrderPending = createSelector(
  getIsLegacyFastSpringBillingOrder,
  getBillingOrderStatus,
  (isCreateSubscriptionBillingOrder, orderStatus) => {
    return isCreateSubscriptionBillingOrder && orderStatus === `pending`;
  }
);

export const isSwitchToAnnual = createSelector(
  getBillingOrder,
  getCurrentPlanName,
  getCurrentBillingPeriod,
  (order, currentPlanName, currentBillingPeriod) => {
    return (
      order.get("billing_period") === "annually" &&
      currentBillingPeriod === "monthly" &&
      order.get("plan") === currentPlanName
    );
  }
);
